import { DomainValidationError } from "./identifiers.js";
import { sha256HexOfText } from "./sha256.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const staffRoles = Object.freeze([
    "concierge",
    "supervisor",
    "catalog_editor",
    "administrator",
]);
export const staffStatuses = Object.freeze(["ACTIVE", "DEACTIVATED"]);
/**
 * The version token of a staff member (CHT-768 review fix 3): a hash of what a screen shows and a command changes, read from persisted rows.
 * It changes on EVERY mutation: a grant makes a new grant row (a new id), a revoke removes one, a deactivation and a reactivation move
 * `status_changed_at`. So a request that was built for an old state is refused even when the role names look the same again (revoke then
 * grant the same role, or deactivate then reactivate and grant again). It holds no name and no role: only ids, a status and a time.
 */
export function staffVersionToken(input) {
    return sha256HexOfText(JSON.stringify([
        input.status,
        input.statusChangedAt === null ? null : input.statusChangedAt.toISOString(),
        [...input.grantIds].sort(),
    ]));
}
export const roleRevokeCauses = Object.freeze(["EXPLICIT", "STAFF_DEACTIVATED"]);
/**
 * The closed registry of system actors. A system transition names one of these as its actor id (the
 * composition root builds the context, never an HTTP caller), and the transition tables say which
 * of them may drive which transition.
 */
export const systemActorIds = Object.freeze([
    "handoff-routing",
    // CHT-734: moves a WAITING handoff to the next coverage (a reassign with a reason code). It can do nothing else.
    "handoff-escalation",
    "ai-handoff-policy",
    "handoff-policy",
    "request-assignment",
    "request-expiry",
    "payment-reconciliation",
    // Phase 2.4. `memory-expiry` and `import-processor` drive only domain and database rules (no use
    // case yet); `trip-clock` starts and completes trips on their local dates.
    "trip-clock",
    "memory-extraction",
    "memory-expiry",
    "import-validator",
    "import-processor",
    // Phase 2.5. `payment-checkout` records the provider order and refund the later provider adapter
    // creates; `payment-expiry` expires unpaid requests on the transaction clock. Neither is the
    // reconciliation service (`payment-reconciliation`), the only actor that moves a request to PAID.
    "payment-checkout",
    "payment-expiry",
    // Phase 3.4. `job-dispatcher` claims due background intents and hands them to the runner. It writes
    // intent state and the dead-letter audit row, never a catalog or import row.
    "job-dispatcher",
    // CHT-732. `messaging-guard` removes a staff message that was written without owning the accepted handoff, and audits it. It writes
    // an audit row and a background intent, never a conversation or handoff row.
    "messaging-guard",
    // Phase 3.5. `place-enricher` verifies that a stored place id still exists and records the result in
    // the enrichment tables. It never writes a catalog item column and never calls the provider for
    // anything but an id-only check.
    "place-enricher",
    // Phase 3.6. `search-indexer` writes search documents and embedding rows (the rebuild and backfill
    // commands, the embedding job). It never writes a catalog item column and holds no catalog authority:
    // it reads customer-visible fields and writes the two search tables only.
    "search-indexer",
    // CHT-763 (Phase 12.3). `request-sla` records an SLA escalation of a request and tells the staff queue or the customer through the outbox.
    // It writes an escalation row, an outbox row and an audit row, never a request row: no stage reassigns a request.
    "request-sla",
    // CHT-771 (Phase 13.5). `privacy-erasure` runs the steps of a deletion request and audits the start and the end of it. It changes the
    // customer's rows only through the erasure function of the database, never through another write.
    "privacy-erasure",
    // CHT-2028. `analytics-retention` removes the visits of the derived analytics read model that are older than 13 months, through the database
    // function `lafayette_analytics_retention_sweep`, and audits the run (counts only). It writes no other row.
    "analytics-retention",
]);
const holdsRole = (user, roles) => user.status === "ACTIVE" && user.activeGrants.some((grant) => roles.includes(grant.role));
/**
 * Can this person take WORK: be the target or owner of a handoff, or the assignee or owner of a request? An ACTIVE staff user with an
 * unrevoked concierge or supervisor grant. The database rule is trigger LF011 (`lafayette_guard_owner_eligible`); a database test
 * (`staff-eligibility.db.test.ts`) keeps the two equal. `null` is a person who does not exist.
 */
export function canTakeWork(user) {
    return user !== null && holdsRole(user, ["concierge", "supervisor"]);
}
/**
 * Can this person hold a concierge ASSIGNMENT (a primary or a backup of a customer)? An ACTIVE staff user with an unrevoked CONCIERGE
 * grant: a supervisor alone cannot. The database rule is trigger LF006 (`lafayette_guard_concierge_assignment_insert`); the same
 * database test keeps the two equal. The routing and the escalation of a handoff judge the holders of assignments with this rule.
 */
export function canHoldAssignment(user) {
    return user !== null && holdsRole(user, ["concierge"]);
}
const MAX_REASON_CHARACTERS = 500;
// A lone surrogate is not valid UTF-8, so PostgreSQL would reject it in a text column.
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
/**
 * The reason a privileged staff operation carries (stored on grants, assignments, status changes
 * and the audit trail): trimmed, 1 to 500 characters (counted by code point, as `char_length`
 * counts them), no NUL and no lone surrogate. The message never contains the value.
 */
export function staffReason(value) {
    if (typeof value !== "string") {
        throw new DomainValidationError("reason", "must be a string");
    }
    const reason = value.trim();
    const characters = [...reason].length;
    if (characters < 1 || characters > MAX_REASON_CHARACTERS) {
        throw new DomainValidationError("reason", "must be 1 to 500 characters");
    }
    if (reason.includes("\u0000") || LONE_SURROGATE.test(reason)) {
        throw new DomainValidationError("reason", "must not contain NUL or unpaired surrogates");
    }
    return reason;
}
